import React from "react";
import styled from "styled-components";
import { colors } from "../../../../theme";
import { POINTER_HOVER_MEDIA } from "../../../styles/pointer-hover";

const Wrapper = styled.div`
  padding-top: 0.5rem;

  @media (min-width: 1024px) {
    padding-top: 0;
  }
`;

const ExperienceList = styled.div`
  word-break: break-word;
`;

const ExperienceItem = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Title = styled.h4`
  font-size: 0.875rem;
  line-height: 1.35;
  font-weight: 700;
  margin: 0;
  color: #111827;

  @media (min-width: 768px) {
    font-size: 1rem;
    line-height: 1.5;
  }
`;

const Link = styled.a`
  display: block;
  margin-top: 4px;
  font-size: 0.8125rem;
  line-height: 1.35;
  color: ${colors.linkHover.light};
  text-decoration: none;
  word-break: break-word;
  transition: color 0.2s ease;

  @media ${POINTER_HOVER_MEDIA} {
    &:hover {
      color: ${colors.linkHover.dark};
      text-decoration: underline;
    }
  }

  &:focus-visible {
    outline: 2px solid
      color-mix(in srgb, ${colors.linkHover.dark} 50%, transparent);
    outline-offset: 2px;
  }

  @media (min-width: 768px) {
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

const Description = styled.div`
  margin-top: 8px;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: #374151;

  @media (min-width: 768px) {
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

export default function ExperienceSection() {
  return (
    <Wrapper>
      <ExperienceList>
        <ExperienceItem>
          <Title>Engage Publisher</Title>
          <Link href="https://meltwater.com/" rel="nofollow">
            https://meltwater.com/
          </Link>
          <Description>
            I served as the lead front-end developer for the publishing web app.
            My role involved overseeing development using Vue, SCSS, Apollo
            GraphQL, and incorporating technologies such as Web workers,
            WebSocket, and i18n. I built over 70 technology-agnostic components
            for an internal design library with Stencil.js (web components), and
            consistently led cross-team front-end initiatives to enhance CI/CD
            flow and improve code quality.
            <br />
            <br />
            On the serverless AWS back end, I managed components including
            Lambda, S3, CloudWatch, SNS, SQS, MongoDB Atlas, and Terraform. I
            conducted performance audits that improved the time to interactive
            (TTI) by 37% and reduced memory usage by 30% using IndexedDB and
            service workers. Additionally, I transitioned CI/CD pipelines from
            Drone to GitHub Actions and implemented preview branches for
            improved deployment processes.
            <br />
            <br />
            In my capacity, I led a team of 5-6 senior software engineers,
            performed code reviews, and maintained ownership of the repository
            code. I also engaged in rotational on-call duties. This was a fully
            remote position, operating in the EST time zone, utilizing Scrum and
            Kanban workflows.
          </Description>
        </ExperienceItem>

        <ExperienceItem>
          <Title>Condexo Pay</Title>
          <Link href="https://pay.condexo.it/" rel="nofollow">
            https://pay.condexo.it/
          </Link>
          <Description>
            I developed a secure front end for a payment aggregator platform
            used in Italy, enabling users to pay bills, send money, buy gift
            cards, and more using React and Material UI. I integrated back-end
            REST APIs and efficiently managed state on the client side using
            Redux and Redux Saga. Additionally, I implemented GDPR-aligned data
            management and consent mechanisms to ensure compliance for all user
            data flows. The work environment followed an Agile workflow,
            operating in the UK time zone, and required in-office collaboration.
          </Description>
        </ExperienceItem>

        <ExperienceItem>
          <Title>Shakespeers</Title>
          <Link href="https://inkwell.younginklings.org/" rel="nofollow">
            https://inkwell.younginklings.org/
          </Link>
          <Description>
            I built a full-stack edtech web app designed to encourage reading
            and writing among kids aged 10-13, utilizing modern technologies
            such as React, Tailwind CSS, RedwoodJS, AWS Lambda, GraphQL, and
            Daisy UI. I implemented interactive components, including a rich
            text format (RTF) editor and drag-and-drop file upload, to enhance
            user engagement. The project involved collaborating with a
            celebrated Forbes 30 under 30 Founder and CTO in a fully remote
            environment, operating in the PST time zone with an Agile workflow.
          </Description>
        </ExperienceItem>

        <ExperienceItem>
          <Title>Condexo Property Management</Title>
          <Link href="https://manager.condexo.co.uk/" rel="nofollow">
            https://manager.condexo.co.uk/
          </Link>
          <Description>
            I developed a property management dashboard web app designed to
            simplify the handling of properties using React, Redux, Redux Saga,
            and React Bootstrap. I integrated back-end REST APIs, as well as
            third-party APIs such as Stripe for payments and Google Maps for
            geolocation services. The working environment involved close
            collaboration with the UI/UX, back-end, and product teams, all
            operating within the UK.
          </Description>
        </ExperienceItem>

        <ExperienceItem>
          <Title>Blank Canvas</Title>
          <Description>
            I worked on redesigning a Web3 website that allows users to paint on
            a digital canvas and mint their creations as NFTs. I utilized
            Next.js, TypeScript, and styled components for the development
            process. In addition, I integrated several crypto wallets and the
            Spotify SDK to enhance user functionality. The project was conducted
            in a fully remote environment, collaborating closely with the
            Founder and the UI/UX team to ensure a pixel-perfect front end,
            operating in the UK time zone.
          </Description>
        </ExperienceItem>

        <ExperienceItem>
          <Title>Blank Auction House</Title>
          <Description>
            I independently developed the MVP for an NFT marketplace, where
            users can buy and sell their digital assets. The project leveraged
            Next.js, TypeScript, Chakra UI, React Query, and Context API to
            build a seamless and interactive user experience. I collaborated
            closely with the Founder and the UI/UX team to ensure a
            pixel-perfect front end.
          </Description>
        </ExperienceItem>

        <ExperienceItem>
          <Title>Harmindra International</Title>
          <Description>
            I built a high-performance JAMstack website using Gatsby, a headless
            CMS, and a CDN to showcase a manufacturing firm's product catalog.
            The site achieved Lighthouse scores of 98+ across performance,
            accessibility, SEO, and best practices. I improved page load time by
            approximately 70% compared to their previous setup and reduced
            server response time to under 100ms through static site generation
            and CDN edge delivery. The serverless architecture and globally
            distributed hosting ensured 100% uptime. Additionally, I designed a
            fully responsive UI rigorously tested across more than 20 device
            breakpoints and browsers.
          </Description>
        </ExperienceItem>

        <ExperienceItem>
          <Title>Credit Culture</Title>
          <Link href="https://creditculture.sg/" rel="nofollow">
            https://creditculture.sg/
          </Link>
          <Description>
            I developed the front end and back end of a fintech money lending
            web application utilizing React, Redux, Redux Thunk, AWS Lambda, AWS
            API Gateway, and Amazon DynamoDB. I implemented features such as
            Single Sign-On (SSO) and a multi-device session workflow to enhance
            user experience. The working environment involved close
            collaboration with senior front-end engineers, operating in the
            Singapore time zone with an in-office setting.
          </Description>
        </ExperienceItem>

        <ExperienceItem>
          <Title>Agrohub</Title>
          <Description>
            I developed a React-based front end for a marketplace website
            tailored to the agriculture domain. I managed state effectively
            using Redux and handled side-effects with Redux Thunk. Reusable
            components were built using Material UI to ensure a cohesive design
            and optimal performance. The project was conducted in a 100% remote
            working environment, operating in the IST time zone.
          </Description>
        </ExperienceItem>

        <ExperienceItem>
          <Title>Cryptlist</Title>
          <Description>
            I developed a portfolio tracking web app for digital assets using
            Next.js, TypeScript, Chakra UI, and Firebase. My role focused on
            designing and developing the front end to provide a seamless user
            experience. I ensured efficient re-renders using server-sent events
            and Zustand, and implemented caching strategies with React Query to
            optimize performance. The project was executed in a fully remote
            environment following an Agile workflow.
          </Description>
        </ExperienceItem>

        <ExperienceItem>
          <Title>Leading airline's loyalty program</Title>
          <Description>
            I developed the front end to support a scalable AWS back end capable
            of handling over 100,000 active users. The front end was built using
            React and Material UI. I implemented optimistic updates for a
            smoother user experience, leveraging SWR. The application was
            deployed to Amazon S3 with CloudFront for content delivery.
            Additionally, I set up the CI/CD workflow using GitHub Actions to
            streamline the deployment process.
          </Description>
        </ExperienceItem>
      </ExperienceList>
    </Wrapper>
  );
}
